// src/cockpit4/components/BusinessMeetingSimulator.tsx
// ============================================
// 经营会议模拟（P1-功能7）- v2.0 详细改进计划
//
// 设计：AI 模拟经营会议
//   1. 一键生成经营周报会议按钮
//   2. 会议流程分步展示：
//      - 第一步：各部门数据汇总（CMO/CSO/CFO/COO 四个部门）
//      - 第二步：AI 生成本部门汇报内容（从 useDashboardData 获取真实数据生成汇报）
//      - 第三步：AI CEO 点评和总结
//      - 第四步：生成行动项清单
//   3. 流式输出效果（用 setInterval 模拟逐字显示）
//   4. 会议纪要持久化到 Vault：10_Operations（运营层）/Meetings/{date}-经营周报.md
//   5. 历史会议记录列表（从 Vault 读取 Meetings 目录）
//
// 数据来源：useDashboardData 获取真实数据生成汇报内容
// ============================================

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Play,
  Loader2,
  RefreshCw,
  FileText,
  Clock,
  CheckCircle2,
  ArrowRight,
  Briefcase,
  DollarSign,
  Mic,
  Megaphone,
  Cog,
} from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";

// ============================================
// 类型定义
// ============================================

/** 会议步骤 */
type MeetingStep = "idle" | "summary" | "reports" | "ceo_review" | "actions" | "done";

/** 部门 ID */
type DepartmentId = "CMO" | "CSO" | "CFO" | "COO";

/** 部门汇报内容 */
interface DepartmentReport {
  /** 部门 ID */
  id: DepartmentId;
  /** 部门中文名 */
  name: string;
  /** 汇报内容 */
  content: string;
  /** 数据汇总（关键指标） */
  metrics: { label: string; value: string; status: "good" | "warning" | "bad" }[];
  /** 图标 */
  icon: typeof DollarSign;
  /** 主题色 */
  color: string;
}

/** 行动项 */
interface ActionItem {
  /** 标题 */
  title: string;
  /** 负责部门 */
  owner: DepartmentId | "CEO";
  /** 截止日 */
  dueDate: string;
  /** 优先级 */
  priority: "high" | "medium" | "low";
}

/** 历史会议记录 */
interface MeetingRecord {
  /** 文件名 */
  name: string;
  /** 路径 */
  path: string;
  /** 修改时间 */
  mtime: string;
}

// ============================================
// 常量配置
// ============================================

const DEPARTMENT_CONFIG: Record<DepartmentId, { name: string; icon: typeof DollarSign; color: string; role: string }> = {
  CMO: { name: "首席营销官", icon: Megaphone, color: "#EC4899", role: "Marketing" },
  CSO: { name: "首席销售官", icon: Users, color: "#3B82F6", role: "Sales / CRM" },
  CFO: { name: "首席财务官", icon: DollarSign, color: "#10B981", role: "Finance" },
  COO: { name: "首席运营官", icon: Cog, color: "#F59E0B", role: "Operations" },
};

const STEP_CONFIG: Record<MeetingStep, { label: string; index: number }> = {
  idle: { label: "待开始", index: 0 },
  summary: { label: "数据汇总", index: 1 },
  reports: { label: "部门汇报", index: 2 },
  ceo_review: { label: "CEO 点评", index: 3 },
  actions: { label: "行动项", index: 4 },
  done: { label: "已完成", index: 5 },
};

// ============================================
// 主组件
// ============================================

export default function BusinessMeetingSimulator() {
  const dashboard = useDashboardData();
  const ceoData = dashboard.ceo;
  const crmData = dashboard.crm;
  const marketingData = dashboard.marketing;

  const [step, setStep] = useState<MeetingStep>("idle");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [reports, setReports] = useState<DepartmentReport[]>([]);
  const [ceoReview, setCeoReview] = useState<string>("");
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [history, setHistory] = useState<MeetingRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // 流式输出控制
  const [streamingText, setStreamingText] = useState<string>("");
  const [streamingTarget, setStreamingTarget] = useState<string>("");
  const streamTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 自动滚动到底部
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [streamingText, reports, ceoReview, actionItems]);

  // ============================================
  // 加载历史会议记录（从 Vault）
  // ============================================
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const resp = await fetch(
        `/vault-api/list?dir=${encodeURIComponent("10_Operations（运营层）/Meetings")}&ext=.md`
      );
      if (!resp.ok) {
        setHistory([]);
        return;
      }
      const json = await resp.json();
      const files: MeetingRecord[] = Array.isArray(json?.files)
        ? json.files
            .filter((f: any) => !f.isDirectory)
            .sort((a: any, b: any) => String(b.mtime || "").localeCompare(String(a.mtime || "")))
            .map((f: any) => ({ name: f.name, path: f.path, mtime: f.mtime || "" }))
        : [];
      setHistory(files);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // ============================================
  // 流式输出工具：逐字显示文本
  // ============================================
  const streamText = useCallback((text: string, onDone?: () => void) => {
    if (streamTimerRef.current) {
      clearInterval(streamTimerRef.current);
    }
    setStreamingText("");
    setStreamingTarget(text);
    let i = 0;
    const chunkSize = Math.max(1, Math.floor(text.length / 200)); // 总约 200 个 chunk
    streamTimerRef.current = setInterval(() => {
      i += chunkSize;
      if (i >= text.length) {
        setStreamingText(text);
        if (streamTimerRef.current) {
          clearInterval(streamTimerRef.current);
          streamTimerRef.current = null;
        }
        onDone?.();
      } else {
        setStreamingText(text.slice(0, i));
      }
    }, 30);
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    };
  }, []);

  // ============================================
  // 生成部门汇报内容（基于真实数据）
  // ============================================
  const generateDepartmentReports = useCallback((): DepartmentReport[] => {
    const kpi = ceoData.kpi;
    const stats = crmData.stats;
    const pipeline = crmData.pipeline;
    const wechat = marketingData.platforms.wechat_official;
    const xiaohongshu = marketingData.platforms.xiaohongshu;

    // CMO：营销汇报
    const cmoMetrics: DepartmentReport["metrics"] = [
      { label: "公众号阅读量", value: `${wechat.metrics.views}`, status: wechat.metrics.views > 0 ? "good" : "warning" },
      { label: "公众号粉丝", value: `${wechat.metrics.followersTotal}`, status: wechat.metrics.followersTotal > 0 ? "good" : "warning" },
      { label: "小红书阅读量", value: `${xiaohongshu.metrics.views}`, status: xiaohongshu.metrics.views > 0 ? "good" : "warning" },
      { label: "内容产出数", value: `${wechat.topContent.length + xiaohongshu.topContent.length} 篇`, status: "good" },
    ];
    const cmoContent = [
      `本周营销工作汇报：`,
      ``,
      `1. 内容产出方面：本周共产出 ${wechat.topContent.length + xiaohongshu.topContent.length} 篇内容，其中公众号 ${wechat.topContent.length} 篇，小红书 ${xiaohongshu.topContent.length} 篇。`,
      `2. 数据表现：公众号累计阅读量 ${wechat.metrics.views}，粉丝总数 ${wechat.metrics.followersTotal}，本周新增粉丝 ${wechat.metrics.followersGain}。`,
      `3. 小红书方面：累计阅读量 ${xiaohongshu.metrics.views}，点赞 ${xiaohongshu.metrics.likes}，收藏 ${xiaohongshu.metrics.favorites}。`,
      `4. 内容 ROI：${kpi.contentRoi}%，活跃品牌数 ${kpi.activeBrands} 个。`,
      ``,
      ceoData.degraded
        ? `⚠️ 注意：营销数据源部分降级，部分指标可能为兜底数据，需后续核实。`
        : `本周内容矩阵稳定推进，建议下周加大小红书种草内容投放，强化品牌曝光。`,
    ].join("\n");

    // CSO：销售/CRM 汇报
    const totalPipeline = pipeline.reduce((s, p) => s + p.count, 0);
    const totalAmount = pipeline.reduce((s, p) => s + p.amount, 0);
    const closingCount = crmData.closingThisWeek.length;
    const csoMetrics: DepartmentReport["metrics"] = [
      { label: "客户总数", value: `${stats?.total || 0}`, status: (stats?.total || 0) > 0 ? "good" : "warning" },
      { label: "线索阶段", value: `${pipeline[0]?.count || 0} 个`, status: "good" },
      { label: "本周预计成交", value: `${closingCount} 单`, status: closingCount > 0 ? "good" : "bad" },
      { label: "Pipeline 总额", value: `¥${totalAmount.toLocaleString()}`, status: totalAmount > 0 ? "good" : "warning" },
    ];
    const csoContent = [
      `本周销售工作汇报：`,
      ``,
      `1. 客户概况：CRM 系统共管理 ${stats?.total || 0} 个客户，本周新增 ${kpi.newCustomers} 人，新增线索 ${kpi.newLeads} 条。`,
      `2. 销售漏斗：Pipeline 共 ${totalPipeline} 个商机，总额 ¥${totalAmount.toLocaleString()}。各阶段分布如下：`,
      ...pipeline.slice(0, 5).map((p) => `   - ${p.label}：${p.count} 个（¥${p.amount.toLocaleString()}）`),
      `3. 本周预计成交：${closingCount} 单，预计金额 ¥${crmData.closingThisWeek.reduce((s, d) => s + d.amount, 0).toLocaleString()}。`,
      `4. 转化率：${kpi.conversionRate}%，${kpi.conversionRate >= 30 ? "处于健康水平" : kpi.conversionRate >= 10 ? "有提升空间" : "需重点优化"}。`,
      ``,
      closingCount === 0
        ? `⚠️ 警示：本周无快成交客户，建议加强商机推进和客户跟进。`
        : `建议：优先跟进本周预计成交的 ${closingCount} 个客户，确保签约落地。`,
    ].join("\n");

    // CFO：财务汇报
    const cfoMetrics: DepartmentReport["metrics"] = [
      { label: "今日收入", value: `¥${kpi.revenue.toLocaleString()}`, status: kpi.revenue >= 1000 ? "good" : kpi.revenue > 0 ? "warning" : "bad" },
      { label: "今日利润", value: `¥${kpi.profit.toLocaleString()}`, status: kpi.profit > 0 ? "good" : "bad" },
      { label: "现金流", value: `¥${kpi.cash.toLocaleString()}`, status: kpi.cash > 0 ? "good" : "bad" },
      { label: "数据状态", value: ceoData.degraded ? "降级" : "正常", status: ceoData.degraded ? "warning" : "good" },
    ];
    const cfoContent = [
      `本周财务工作汇报：`,
      ``,
      `1. 收入情况：今日收入 ¥${kpi.revenue.toLocaleString()}，${kpi.revenue >= 1000 ? "达到日收入目标" : kpi.revenue > 0 ? "低于日收入目标 1000 元" : "今日暂无收入入账"}。`,
      `2. 利润情况：今日毛利 ¥${kpi.profit.toLocaleString()}，${kpi.profit > 0 ? "保持盈利" : "处于亏损状态，需控制成本"}。`,
      `3. 现金流：当前现金流 ¥${kpi.cash.toLocaleString()}，${kpi.cash > 0 ? "可支撑运营" : "需关注资金周转"}。`,
      `4. 数据健康度：${ceoData.degraded ? "部分财务数据源降级，使用兜底数据，建议尽快修复" : "所有财务数据源正常"}。`,
      ``,
      kpi.revenue < 1000
        ? `⚠️ 建议：本周收入低于预期，建议联合 CMO 加强内容变现，联合 CSO 加快客户签约。`
        : `本周财务状况稳定，建议继续保持成本控制和收入增长节奏。`,
    ].join("\n");

    // COO：运营汇报
    const cooMetrics: DepartmentReport["metrics"] = [
      { label: "Pipeline 商机", value: `${totalPipeline} 个`, status: totalPipeline > 0 ? "good" : "warning" },
      { label: "活跃品牌", value: `${kpi.activeBrands} 个`, status: kpi.activeBrands > 0 ? "good" : "warning" },
      { label: "高优客户", value: `${crmData.priorityCustomers.length} 个`, status: crmData.priorityCustomers.length > 0 ? "good" : "warning" },
      { label: "营销平台", value: `6 个`, status: "good" },
    ];
    const cooContent = [
      `本周运营工作汇报：`,
      ``,
      `1. 整体运营：本周各系统运行${ceoData.degraded ? "存在部分降级" : "正常"}，活跃品牌 ${kpi.activeBrands} 个，覆盖 6 大营销平台。`,
      `2. 客户运营：高优先级客户 ${crmData.priorityCustomers.length} 个，Pipeline 商机 ${totalPipeline} 个，本周预计成交 ${closingCount} 单。`,
      `3. 内容运营：本周内容产出 ${wechat.topContent.length + xiaohongshu.topContent.length} 篇，公众号粉丝 ${wechat.metrics.followersTotal}，小红书粉丝 ${xiaohongshu.metrics.followersTotal}。`,
      `4. 流程效率：销售漏斗转化率 ${kpi.conversionRate}%，内容 ROI ${kpi.contentRoi}%。`,
      ``,
      ceoData.degraded
        ? `⚠️ 建议：部分 Runtime 降级，需尽快检查 EnterpriseBrain / KnowledgeRuntime / CRM 等模块状态。`
        : `整体运营效率良好，建议下周聚焦高优客户转化和内容变现能力提升。`,
    ].join("\n");

    return [
      {
        id: "CMO",
        name: DEPARTMENT_CONFIG.CMO.name,
        content: cmoContent,
        metrics: cmoMetrics,
        icon: DEPARTMENT_CONFIG.CMO.icon,
        color: DEPARTMENT_CONFIG.CMO.color,
      },
      {
        id: "CSO",
        name: DEPARTMENT_CONFIG.CSO.name,
        content: csoContent,
        metrics: csoMetrics,
        icon: DEPARTMENT_CONFIG.CSO.icon,
        color: DEPARTMENT_CONFIG.CSO.color,
      },
      {
        id: "CFO",
        name: DEPARTMENT_CONFIG.CFO.name,
        content: cfoContent,
        metrics: cfoMetrics,
        icon: DEPARTMENT_CONFIG.CFO.icon,
        color: DEPARTMENT_CONFIG.CFO.color,
      },
      {
        id: "COO",
        name: DEPARTMENT_CONFIG.COO.name,
        content: cooContent,
        metrics: cooMetrics,
        icon: DEPARTMENT_CONFIG.COO.icon,
        color: DEPARTMENT_CONFIG.COO.color,
      },
    ];
  }, [ceoData, crmData, marketingData]);

  // ============================================
  // 生成 CEO 点评
  // ============================================
  const generateCEOReview = useCallback((reports: DepartmentReport[]): string => {
    const kpi = ceoData.kpi;
    const totalRevenue = kpi.revenue;
    const totalCustomers = kpi.newCustomers;
    const conversionRate = kpi.conversionRate;
    const closingCount = crmData.closingThisWeek.length;

    return [
      `## CEO 综合点评`,
      ``,
      `各位负责人的汇报都很到位，我对本周经营情况做以下总结：`,
      ``,
      `### 一、整体表现`,
      ``,
      `- 财务方面：本周收入 ¥${totalRevenue.toLocaleString()}，${totalRevenue >= 1000 ? "达到预期目标" : "未达预期目标"}，${kpi.profit > 0 ? "保持盈利" : "需关注盈利能力"}。`,
      `- 客户方面：本周新增客户 ${totalCustomers} 人，转化率 ${conversionRate}%，${conversionRate >= 30 ? "转化效率良好" : "转化效率有待提升"}。`,
      `- 内容方面：${reports[0].metrics[3].value} 内容产出，ROI ${kpi.contentRoi}%。`,
      `- 运营方面：Pipeline 商机稳定，本周预计成交 ${closingCount} 单。`,
      ``,
      `### 二、关键问题`,
      ``,
      ...(totalRevenue < 1000 ? [`- ⚠️ 收入低于日目标 1000 元，需 CMO 和 CSO 联合制定变现方案`] : []),
      ...(conversionRate < 30 ? [`- ⚠️ 转化率 ${conversionRate}% 偏低，CSO 需排查销售流程瓶颈`] : []),
      ...(closingCount === 0 ? [`- ⚠️ 本周无快成交客户，需加强商机推进`] : []),
      ...(ceoData.degraded ? [`- ⚠️ 部分数据源降级，COO 需协调技术团队修复`] : []),
      ...(totalRevenue >= 1000 && conversionRate >= 30 && closingCount > 0 && !ceoData.degraded
        ? [`- ✅ 各项指标健康，继续保持当前节奏`]
        : []),
      ``,
      `### 三、下周重点`,
      ``,
      `1. CMO：加大内容投放力度，提升品牌曝光和粉丝增长`,
      `2. CSO：聚焦 ${closingCount} 个快成交客户，确保签约落地`,
      `3. CFO：监控现金流，控制成本支出`,
      `4. COO：${ceoData.degraded ? "修复数据源降级问题" : "优化运营流程，提升效率"}`,
      ``,
      `各位加油！下周同一时间再开会复盘。`,
    ].join("\n");
  }, [ceoData, crmData]);

  // ============================================
  // 生成行动项
  // ============================================
  const generateActionItems = useCallback((reports: DepartmentReport[], ceoReview: string): ActionItem[] => {
    const kpi = ceoData.kpi;
    const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const dayAfter = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const items: ActionItem[] = [];

    // 基于数据自动派生行动项
    if (kpi.revenue < 1000) {
      items.push({
        title: "制定收入提升方案（联合营销+销售变现）",
        owner: "CMO",
        dueDate: dayAfter,
        priority: "high",
      });
    }
    if (kpi.conversionRate < 30) {
      items.push({
        title: "排查销售漏斗瓶颈，优化转化流程",
        owner: "CSO",
        dueDate: nextWeek,
        priority: "high",
      });
    }
    if (crmData.closingThisWeek.length > 0) {
      items.push({
        title: `跟进 ${crmData.closingThisWeek.length} 个快成交客户，确保签约`,
        owner: "CSO",
        dueDate: dayAfter,
        priority: "high",
      });
    }
    if (ceoData.degraded) {
      items.push({
        title: "修复数据源降级问题（EnterpriseBrain/Knowledge/CRM）",
        owner: "COO",
        dueDate: dayAfter,
        priority: "high",
      });
    }
    // 通用行动项
    items.push({
      title: "加大内容投放力度，提升公众号和小红书曝光",
      owner: "CMO",
      dueDate: nextWeek,
      priority: "medium",
    });
    items.push({
      title: "监控现金流，控制本周成本支出",
      owner: "CFO",
      dueDate: nextWeek,
      priority: "medium",
    });
    items.push({
      title: "整理本周高优客户跟进记录，制定下周跟进计划",
      owner: "CSO",
      dueDate: nextWeek,
      priority: "medium",
    });
    items.push({
      title: "完善 OKR 进度更新，确保季度目标达成",
      owner: "CEO",
      dueDate: nextWeek,
      priority: "low",
    });

    return items;
  }, [ceoData, crmData]);

  // ============================================
  // 一键生成经营周报会议
  // ============================================
  const startMeeting = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    setReports([]);
    setCeoReview("");
    setActionItems([]);
    setStep("summary");

    try {
      // 第一步：数据汇总（短暂延时模拟）
      await new Promise((r) => setTimeout(r, 600));
      setStep("reports");

      // 第二步：生成部门汇报（流式输出）
      const allReports = generateDepartmentReports();
      for (let i = 0; i < allReports.length; i++) {
        const report = allReports[i];
        setReports((prev) => [...prev, { ...report, content: "" }]);
        // 流式输出当前部门内容
        await new Promise<void>((resolve) => {
          streamText(report.content, () => {
            // 流式输出完成后，将完整内容写回 reports
            setReports((prev) =>
              prev.map((r) => (r.id === report.id ? { ...r, content: report.content } : r))
            );
            setStreamingText("");
            setStreamingTarget("");
            resolve();
          });
        });
        await new Promise((r) => setTimeout(r, 200));
      }

      // 第三步：CEO 点评
      setStep("ceo_review");
      const review = generateCEOReview(allReports);
      await new Promise<void>((resolve) => {
        streamText(review, () => {
          setCeoReview(review);
          setStreamingText("");
          setStreamingTarget("");
          resolve();
        });
      });
      await new Promise((r) => setTimeout(r, 200));

      // 第四步：生成行动项
      setStep("actions");
      const actions = generateActionItems(allReports, review);
      for (let i = 0; i < actions.length; i++) {
        await new Promise((r) => setTimeout(r, 100));
        setActionItems((prev) => [...prev, actions[i]]);
      }

      setStep("done");

      // 持久化会议纪要到 Vault
      const date = new Date().toISOString().slice(0, 10);
      const content = buildMeetingMarkdown(date, allReports, review, actions);
      try {
        await fetch(
          `/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Meetings/${date}-经营周报.md`)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ content }),
          }
        );
        setMessage({
          type: "ok",
          text: `✅ 经营周报会议已生成并写入 Vault：10_Operations（运营层）/Meetings/${date}-经营周报.md`,
        });
        // 刷新历史记录
        void loadHistory();
      } catch (err) {
        setMessage({
          type: "err",
          text: `❌ Vault 写入失败：${(err as Error).message}`,
        });
      }
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ 会议生成失败：${(err as Error).message}`,
      });
      // v6.3.25 回滚到初始状态：原实现 step 停留在非 idle 且 reports 为空 → 正文永久空白
      setStep("idle");
      setReports([]);
      setStreamingText("");
      setStreamingTarget("");
    } finally {
      setLoading(false);
    }
  }, [generateDepartmentReports, generateCEOReview, generateActionItems, streamText, loadHistory]);

  // ============================================
  // 渲染
  // ============================================
  const currentStepIdx = STEP_CONFIG[step].index;
  const totalSteps = 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Users size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">经营会议模拟</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Meeting Simulator · CMO/CSO/CFO/COO · {step === "idle" ? "待开始" : `${currentStepIdx}/${totalSteps} 步`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 历史记录 */}
          <button
            onClick={() => {
              const next = !showHistory;
              setShowHistory(next);
              if (next) void loadHistory();
            }}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md border transition-colors ${
              showHistory
                ? "bg-cyan-500/15 border-cyan-500/30 text-cyan-300"
                : "bg-white/[0.03] border-white/[0.06] text-gray-400 hover:text-gray-300"
            }`}
            title="查看历史会议记录"
          >
            <FileText size={10} />
            <span className="text-[9px] font-mono uppercase tracking-wider">
              历史 {history.length}
            </span>
          </button>
          {/* 一键生成 */}
          <button
            onClick={startMeeting}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-50"
            title="一键生成经营周报会议"
          >
            {loading ? (
              <Loader2 size={11} className="text-cyan-400 animate-spin" />
            ) : (
              <Play size={11} className="text-cyan-400" />
            )}
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
              {loading ? "生成中..." : step === "done" ? "重新生成" : "一键生成周报会议"}
            </span>
          </button>
          <button
            onClick={() => {
              setStep("idle");
              setReports([]);
              setCeoReview("");
              setActionItems([]);
              setStreamingText("");
              setStreamingTarget("");
            }}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
            title="重置"
          >
            <RefreshCw size={11} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono whitespace-pre-line ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      {/* 步骤进度条 */}
      {step !== "idle" && (
        <div className="px-4 py-2 border-b border-white/[0.04] flex items-center gap-2">
          {(["summary", "reports", "ceo_review", "actions", "done"] as MeetingStep[]).map((s, idx) => {
            const isCurrent = step === s;
            const isPast = STEP_CONFIG[s].index < currentStepIdx;
            return (
              <div key={s} className="flex items-center gap-1.5">
                <div
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono uppercase ${
                    isCurrent
                      ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                      : isPast
                      ? "text-emerald-400"
                      : "text-gray-600"
                  }`}
                >
                  {isPast && <CheckCircle2 size={9} />}
                  {isCurrent && loading && <Loader2 size={9} className="animate-spin" />}
                  {STEP_CONFIG[s].label}
                </div>
                {idx < 4 && <ArrowRight size={10} className="text-gray-700" />}
              </div>
            );
          })}
        </div>
      )}

      {/* 内容区 */}
      <div ref={contentRef} className="p-4 max-h-[560px] overflow-y-auto">
        {/* 历史记录视图 */}
        {showHistory ? (
          <div>
            {historyLoading ? (
              <div className="py-8 text-center">
                <Loader2 size={20} className="mx-auto text-cyan-400 animate-spin mb-2" />
                <p className="text-[11px] font-mono text-gray-500">加载历史会议记录...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="py-8 text-center">
                <FileText size={20} className="mx-auto text-gray-700 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">暂无历史会议记录</p>
                <p className="text-[10px] font-mono text-gray-600 mt-1">
                  点击「一键生成周报会议」即可写入 Vault
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {history.map((h) => (
                  <div
                    key={h.path}
                    className="flex items-center gap-2 p-2 rounded-md border border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                  >
                    <FileText size={11} className="text-cyan-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-200 truncate">{h.name}</div>
                      <div className="text-[9px] font-mono text-gray-600">
                        {h.mtime ? h.mtime.slice(0, 16).replace("T", " ") : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : step === "idle" ? (
          /* 空状态 */
          <div className="py-12 text-center">
            <Users size={28} className="mx-auto text-gray-700 mb-3" />
            <p className="text-[12px] font-mono text-gray-500 mb-2">经营会议模拟器</p>
            <p className="text-[10px] font-mono text-gray-600 mb-4">
              点击「一键生成周报会议」开始模拟
            </p>
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-md border border-white/[0.04] bg-white/[0.02]">
              {(Object.keys(DEPARTMENT_CONFIG) as DepartmentId[]).map((id) => {
                const cfg = DEPARTMENT_CONFIG[id];
                const Icon = cfg.icon;
                return (
                  <div key={id} className="flex items-center gap-1">
                    <Icon size={12} style={{ color: cfg.color }} />
                    <span className="text-[10px] font-mono text-gray-400">{id}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-[9px] font-mono text-gray-700 mt-4">
              会议流程：数据汇总 → 部门汇报 → CEO 点评 → 行动项
            </p>
          </div>
        ) : (
          /* 会议内容 */
          <div className="space-y-3">
            {/* 第一步：数据汇总（嵌入到第二步展示中） */}

            {/* 第二步：部门汇报 */}
            {step === "summary" || step === "reports" || step === "ceo_review" || step === "actions" || step === "done" ? (
              reports.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase size={11} className="text-cyan-400" />
                    <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                      部门汇报（{reports.length}/4）
                    </span>
                  </div>
                  <div className="space-y-2">
                    {reports.map((r) => {
                      const Icon = r.icon;
                      const isLast = r.id === reports[reports.length - 1].id;
                      const isStreaming = isLast && step === "reports" && streamingTarget && r.content === "";
                      return (
                        <motion.div
                          key={r.id}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="rounded-md border p-2.5"
                          style={{ borderColor: `${r.color}25`, background: `${r.color}08` }}
                        >
                          {/* 部门头 */}
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-5 h-5 rounded flex items-center justify-center"
                              style={{ background: `${r.color}15` }}
                            >
                              <Icon size={11} style={{ color: r.color }} />
                            </div>
                            <span className="text-[11px] font-semibold text-white">{r.id}</span>
                            <span className="text-[10px] font-mono text-gray-500">· {r.name}</span>
                            <span
                              className="ml-auto px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                              style={{ background: `${r.color}15`, color: r.color }}
                            >
                              {DEPARTMENT_CONFIG[r.id].role}
                            </span>
                          </div>
                          {/* 数据指标 */}
                          <div className="grid grid-cols-4 gap-1.5 mb-2">
                            {r.metrics.map((m, i) => (
                              <div
                                key={i}
                                className="rounded border border-white/[0.04] bg-white/[0.02] px-1.5 py-1"
                              >
                                <div className="text-[8px] font-mono text-gray-600 uppercase truncate">
                                  {m.label}
                                </div>
                                <div
                                  className="text-[10px] font-mono font-bold truncate"
                                  style={{
                                    color:
                                      m.status === "good" ? "#10B981"
                                      : m.status === "warning" ? "#F59E0B"
                                      : "#EF4444",
                                  }}
                                >
                                  {m.value}
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* 汇报内容 */}
                          <pre className="text-[10px] font-mono text-gray-300 whitespace-pre-wrap leading-relaxed">
                            {isStreaming ? streamingText : r.content}
                            {isStreaming && (
                              <span className="inline-block w-1.5 h-3 ml-0.5 bg-cyan-400 animate-pulse align-middle" />
                            )}
                          </pre>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )
            ) : null}

            {/* 第三步：CEO 点评 */}
            {(step === "ceo_review" || step === "actions" || step === "done") && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Mic size={11} className="text-purple-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    CEO 综合点评
                  </span>
                </div>
                <div className="rounded-md border border-purple-500/25 bg-purple-500/[0.04] p-2.5">
                  <pre className="text-[10px] font-mono text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {step === "ceo_review" && streamingTarget ? streamingText : ceoReview}
                    {step === "ceo_review" && streamingTarget && (
                      <span className="inline-block w-1.5 h-3 ml-0.5 bg-purple-400 animate-pulse align-middle" />
                    )}
                  </pre>
                </div>
              </div>
            )}

            {/* 第四步：行动项 */}
            {(step === "actions" || step === "done") && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 size={11} className="text-emerald-400" />
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    行动项清单（{actionItems.length}）
                  </span>
                </div>
                <div className="space-y-1.5">
                  {actionItems.map((item, idx) => {
                    const priorityCfg = {
                      high: { color: "#EF4444", label: "高" },
                      medium: { color: "#F59E0B", label: "中" },
                      low: { color: "#3B82F6", label: "低" },
                    }[item.priority];
                    return (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-2 p-2 rounded-md border border-white/[0.06] bg-white/[0.02]"
                      >
                        <span
                          className="flex-shrink-0 w-1 h-4 rounded-full"
                          style={{ background: priorityCfg.color }}
                        />
                        <span className="text-[11px] text-gray-200 flex-1">{item.title}</span>
                        <span
                          className="flex-shrink-0 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                          style={{ background: `${priorityCfg.color}15`, color: priorityCfg.color }}
                        >
                          {item.owner}
                        </span>
                        <span className="flex-shrink-0 text-[9px] font-mono text-gray-600 flex items-center gap-0.5">
                          <Clock size={8} />
                          {item.dueDate}
                        </span>
                        <span
                          className="flex-shrink-0 px-1 py-0.5 rounded text-[8px] font-mono"
                          style={{ background: `${priorityCfg.color}10`, color: priorityCfg.color }}
                        >
                          {priorityCfg.label}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// 工具函数：生成会议纪要 Markdown
// ============================================

function buildMeetingMarkdown(
  date: string,
  reports: DepartmentReport[],
  ceoReview: string,
  actions: ActionItem[]
): string {
  return [
    `---`,
    `type: business_meeting`,
    `meeting_type: weekly_review`,
    `date: ${date}`,
    `created_at: ${new Date().toISOString()}`,
    `departments: ${reports.map((r) => r.id).join(",")}`,
    `action_items: ${actions.length}`,
    `---`,
    ``,
    `# 🎙️ 经营周报会议 · ${date}`,
    ``,
    `> 生成时间：${new Date().toLocaleString("zh-CN", { hour12: false })}`,
    `> 参会人员：CMO / CSO / CFO / COO / CEO`,
    ``,
    `## 一、各部门汇报`,
    ``,
    ...reports.map((r) => {
      const lines: string[] = [];
      lines.push(`### ${r.id} · ${r.name}`);
      lines.push(``);
      lines.push(`**关键指标：**`);
      r.metrics.forEach((m) => {
        lines.push(`- ${m.label}：${m.value}（${m.status === "good" ? "✅ 良好" : m.status === "warning" ? "⚠️ 关注" : "❌ 异常"}）`);
      });
      lines.push(``);
      lines.push(`**汇报内容：**`);
      lines.push(``);
      lines.push(r.content);
      lines.push(``);
      return lines.join("\n");
    }),
    ``,
    `## 二、CEO 综合点评`,
    ``,
    ceoReview,
    ``,
    `## 三、行动项清单`,
    ``,
    ...actions.map((a, i) => {
      return `${i + 1}. **${a.title}**（负责人：${a.owner}，截止：${a.dueDate}，优先级：${a.priority === "high" ? "高" : a.priority === "medium" ? "中" : "低"}）`;
    }),
    ``,
    `---`,
    `会议纪要由 CEO Workspace · Business Meeting Simulator 自动生成`,
  ].join("\n");
}
